import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { CheckIcon } from 'lucide-react';
import { forwardRef, type ComponentProps, type ComponentPropsWithRef, type ReactNode } from 'react';
import { portaledSurfaceVariants, type PortaledSurfaceVariant } from '../lib/portaledSurface';
import { cn } from '../lib/utils';
import {
  ComboboxChipsInput as UiComboboxChipsInput,
  ComboboxContent as UiComboboxContent,
  ComboboxInput as UiComboboxInput,
  ComboboxList as UiComboboxList,
} from './ui/combobox';

type ComboboxListProps<G> = Omit<ComponentProps<typeof UiComboboxList>, 'children'> & {
  children?: ReactNode | ((group: G, index: number) => ReactNode);
};

export function ComboboxList<G>(props: ComboboxListProps<G>) {
  return <UiComboboxList {...(props as ComponentProps<typeof UiComboboxList>)} />;
}

type ComboboxContentProps = ComponentProps<typeof UiComboboxContent> & {
  /** Light (default) or dark surface. Pass `"dark"` for a combobox opened from inside a `DarkThemeScope` — it portals to `document.body` and so escapes the scope's own `.dark`. */
  variant?: PortaledSurfaceVariant;
};

export function ComboboxContent({ variant = 'light', className, ...props }: ComboboxContentProps) {
  return (
    <UiComboboxContent
      {...props}
      className={cn(
        'tw',
        portaledSurfaceVariants({ variant }),
        // Override ui/combobox nested popup search `bg-input/30` → Figma surface.
        // Use [&_…] — `*:data-[slot=…]` compiles to .class[data-slot=input-group] > *
        // and never matches ComboboxContent (data-slot=combobox-content).
        '[&_[data-slot=input-group]]:border-input [&_[data-slot=input-group]]:bg-background-surface',
        className,
      )}
    />
  );
}

export function ComboboxItem({
  className,
  children,
  checkbox = false,
  ...props
}: ComboboxPrimitive.Item.Props & { checkbox?: boolean }) {
  return (
    <ComboboxPrimitive.Item
      data-slot="combobox-item"
      className={cn(
        'group/item outline-hidden relative flex w-full cursor-default select-none items-center gap-2 rounded-sm px-2 py-1.5 text-sm data-[disabled]:pointer-events-none data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    >
      {checkbox && (
        <span className="shadow-xs flex size-4 shrink-0 items-center justify-center rounded border border-input transition-shadow group-data-[selected]/item:border-primary group-data-[selected]/item:bg-primary group-data-[selected]/item:text-primary-foreground">
          <ComboboxPrimitive.ItemIndicator>
            <CheckIcon className="size-3.5" />
          </ComboboxPrimitive.ItemIndicator>
        </span>
      )}
      {children}
    </ComboboxPrimitive.Item>
  );
}

/**
 * Chip row used as a popup anchor (`ref={anchor}`). Render `ComboboxPrimitive.Chips`
 * directly — `ui/combobox`'s `ComboboxChips` is a plain function component, so a
 * JSX `ref` never reaches the primitive on React 18.
 */
export const ComboboxChips = forwardRef<
  HTMLDivElement,
  ComponentPropsWithRef<typeof ComboboxPrimitive.Chips>
>(function ComboboxChips({ className, ...props }, ref) {
  return (
    <ComboboxPrimitive.Chips
      data-slot="combobox-chips"
      {...props}
      ref={ref}
      className={cn(
        // Keep in sync with `ui/combobox` ComboboxChips; `bg-clip-border` replaces
        // shadcn's `bg-clip-padding` so the fill covers the full border box.
        // Override ui/combobox chips `dark:bg-input/30` → Figma `background/surface`.
        'shadow-xs has-aria-invalid:border-destructive has-aria-invalid:ring-[3px] has-aria-invalid:ring-destructive/20 has-data-[slot=combobox-chip]:px-1.5 dark:has-aria-invalid:border-destructive/50 dark:has-aria-invalid:ring-destructive/40 flex min-h-9 flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent bg-clip-border px-2.5 py-1.5 text-sm transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 dark:bg-background-surface',
        className,
      )}
    />
  );
});

/** Keep the inline search input visually part of the chip container (no native field fill). */
export function ComboboxChipsInput({
  className,
  ...props
}: ComponentProps<typeof UiComboboxChipsInput>) {
  return (
    <UiComboboxChipsInput
      {...props}
      className={cn(
        'm-0 h-auto min-h-0 appearance-none border-0 bg-transparent p-0 shadow-none dark:!bg-transparent',
        className,
      )}
    />
  );
}

/** Single-value combobox field — kill InputGroup's `dark:bg-input/30`. */
export function ComboboxInput({ className, ...props }: ComponentProps<typeof UiComboboxInput>) {
  return <UiComboboxInput {...props} className={cn('dark:bg-background-surface', className)} />;
}

export {
  Combobox,
  ComboboxChip,
  ComboboxCollection,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxLabel,
  ComboboxSeparator,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
} from './ui/combobox';
